import { Col, Grid, Pill, Row, Skeleton, Typography } from "@codat/orchard-ui";
import { clsx } from "clsx";
import * as React from "react";

import { sendAmplitudeEvent } from "../../amplitude";
import { ConnectorStatusSummary } from "../../Models";
import { CompanyStatusType } from "../../utilsCompanyStatus";
import { ConnectionStatusType } from "../../utilsConnectorStatus";
import styles from "./ConnectorCountsBar.module.css";

interface IConnectorCountsBarProps {
  connectors: ConnectorStatusSummary[];
  isLoading: boolean;
  syncingConnectors: number;
  onSyncFilter: (status: CompanyStatusType) => void;
  connectorStatusFilter: string;
  onConnectorFilter: (status: ConnectionStatusType) => void;
  syncStatusFilter: string;
}

const ConnectorCountsBar: React.FC<IConnectorCountsBarProps> = ({
  connectors,
  isLoading,
  onSyncFilter,
  syncingConnectors,
  connectorStatusFilter,
  onConnectorFilter,
  syncStatusFilter,
}) => {
  const aliveConnector = connectors.find(
    (connector) => connector.status === "alive"
  );
  const unknownConnector = connectors.find(
    (connector) => connector.status === "unknown"
  );

  const filterActiveConnectors = () => {
    onConnectorFilter("alive");
    sendAmplitudeEvent({
      action: "Click",
      element: "Filter Active Companies",
    });
  };

  const filterSyncingConnectors = () => {
    onSyncFilter("Syncing");
    sendAmplitudeEvent({
      action: "Click",
      element: "Filter Syncing Companies",
    });
  };

  const filterUnknownConnectors = () => {
    onConnectorFilter("unknown");
    sendAmplitudeEvent({
      action: "Click",
      element: "Filter Unknown Companies",
    });
  };

  return (
    <Grid className={styles.grid}>
      <div className={styles.innerContainer}>
        <Typography className={styles.lastSeen} as="h2">
          Last seen status
        </Typography>
        <Grid>
          <Row>
            {isLoading ? (
              <Col className={styles.skeleton}>
                <Skeleton
                  width="37.75rem"
                  height="7rem"
                  data-testid="skeleton"
                />
              </Col>
            ) : (
              <>
                <Col size={4}>
                  <Pill
                    label="Active"
                    onClick={filterActiveConnectors}
                    content={String(aliveConnector?.count ?? 0)}
                    className={clsx(styles.pill, {
                      [styles.activePill]: connectorStatusFilter === "alive",
                    })}
                    data-testid="state_alive"
                  />
                </Col>
                <Col size={4}>
                  <Pill
                    label="Syncing"
                    content={String(syncingConnectors)}
                    onClick={filterSyncingConnectors}
                    className={clsx(styles.pill, {
                      [styles.activePill]: syncStatusFilter === "Syncing",
                    })}
                    data-testid="state_syncing"
                  />
                </Col>
                <Col size={4}>
                  <Pill
                    data-testid={
                      !isLoading && unknownConnector
                        ? `state_${unknownConnector.status}`
                        : "state_unknown"
                    }
                    label="Unknown"
                    content={String(unknownConnector?.count ?? 0)}
                    className={clsx(styles.pill, {
                      [styles.activePill]: connectorStatusFilter === "unknown",
                    })}
                    onClick={filterUnknownConnectors}
                  />
                </Col>
              </>
            )}
          </Row>
        </Grid>
      </div>
    </Grid>
  );
};

export default ConnectorCountsBar;
